'use client'

import { useEffect, useRef } from 'react'
import { useSocket } from '@/providers/SocketContext'
import { USER_IDLE_MS, isUserActive, observeUserActivity } from '@/lib/presence/userActivity'
import type { PresenceActivity } from '@/types/presence'

const KEEPALIVE_MS = 90 * 1000

/**
 * Pré-requisito da presença: emite `presence_update {active|afk}` conforme atividade da aba.
 * 5min ocioso → afk; atividade → active; keepalive re-emite o status atual a cada ~90s.
 * NUNCA emite offline; não usa beforeunload/pagehide.
 *
 * **O rastreio de atividade não mora mais aqui** — foi extraído para
 * [[../../lib/presence/userActivity]] em 2026-09-04, quando a decisão
 * "toast ou notificação do SO" passou a precisar do mesmo fato. Este hook
 * continua dono do TIMER e do protocolo de presença; dono do sinal é o módulo.
 */
export function useIdleDetector(options?: { idleMs?: number; keepaliveMs?: number }) {
  const { socket, socketReady } = useSocket()
  const idleMs = options?.idleMs ?? USER_IDLE_MS
  const keepaliveMs = options?.keepaliveMs ?? KEEPALIVE_MS
  const statusRef = useRef<PresenceActivity>('active')

  useEffect(() => {
    if (!socket || !socketReady) return
    let idleTimer: ReturnType<typeof setTimeout>

    const emit = (status: PresenceActivity) => {
      statusRef.current = status
      socket.emit('presence_update', { status })
    }
    const armIdle = () => {
      clearTimeout(idleTimer)
      idleTimer = setTimeout(() => emit('afk'), idleMs)
    }
    const onActivity = () => {
      if (statusRef.current !== 'active') emit('active')
      armIdle()
    }

    // Estado inicial — e em TODA reconexão: o effect re-roda quando `socketReady` volta a
    // `true`. Emite o status REAL de atividade, não `active` fixo: o back mantém o `afk` de
    // quem reconecta dentro do grace (faixa 24 §3) e um `active` fixo aqui anulava isso no
    // primeiro evento. `statusRef` não serve de fonte — é o último EMITIDO, não o que a
    // pessoa está fazendo.
    const inicial: PresenceActivity = isUserActive(idleMs) ? 'active' : 'afk'
    emit(inicial)
    // Ocioso não arma o timer: já está afk, e a próxima atividade arma (`onActivity`).
    if (inicial === 'active') armIdle()

    // `visibilitychange` → visível também chega por aqui: o módulo trata a VOLTA
    // da aba como atividade (a ida, não).
    const parar = observeUserActivity(onActivity)
    const keepalive = setInterval(
      () => socket.emit('presence_update', { status: statusRef.current }),
      keepaliveMs,
    )

    return () => {
      clearTimeout(idleTimer)
      clearInterval(keepalive)
      parar()
    }
  }, [socket, socketReady, idleMs, keepaliveMs])
}
